import { SuggestionData } from 'ckeditor5-premium-features'
import { CHANGE_TYPE_MAP } from 'components/WorkspaceEditor/components/TrackChangesPanel/hooks/useSuggestionDataHandler'
import { useTrackChangesCardStyles } from 'components/WorkspaceEditor/components/TrackChangesPanel/styles/useTrackChangesCardStyles'
import { CardType } from 'components/WorkspaceEditor/components/TrackChangesPanel/types/types'
import {
    TrackChangeStatus,
    TrackChangeType,
    TrackedChange,
} from 'models/changes'
import { useMemo } from 'react'

interface UseCardTypeResult {
    cardType: CardType
    classes: ReturnType<typeof useTrackChangesCardStyles>
}

const SUGGESTION_TYPE_MAP = {
    INSERTION: CHANGE_TYPE_MAP[TrackChangeType.ADDED],
    DELETION: CHANGE_TYPE_MAP[TrackChangeType.DELETED],
    REPLACED: CHANGE_TYPE_MAP[TrackChangeType.REPLACED],
    DISABLED: CHANGE_TYPE_MAP[TrackChangeType.DISABLED],
    MODIFIED: CHANGE_TYPE_MAP[TrackChangeType.MODIFIED],
} as const

/**
 * Custom hook to determine the type of a track change card and return associated styles.
 *
 * @param {SuggestionData} cardSuggestion - The suggestion data for the card.
 * @param {boolean} isNotDefaultSuggestionCardFilter - Flag to indicate if the default suggestion card filter is not applied.
 * @param {TrackedChange} [trackChanges] - Optional tracked change data.
 * @returns {UseCardTypeResult} An object containing the card type and associated styles.
 */
export const useCardType = (
    cardSuggestion: SuggestionData,
    isNotDefaultSuggestionCardFilter: boolean,
    trackChanges?: TrackedChange
): UseCardTypeResult => {
    const { cardType, isAddedCardType } = useMemo(() => {
        const suggestionType = cardSuggestion?.type?.toLowerCase() || ''
        const changeType = trackChanges?.changeType
        const trackChangeStatus = trackChanges?.status?.toUpperCase()
        const isApproved = trackChangeStatus === TrackChangeStatus.APPROVED
        const isRejected = trackChangeStatus === TrackChangeStatus.REJECTED
        const isChangeTypeOptionGroup =
            changeType === TrackChangeType.OPTIONGROUP
        const isChangeTypeDeleted = changeType === TrackChangeType.DELETED
        const isChangeTypeAdded = changeType === TrackChangeType.ADDED
        const isChangeTypeModified = changeType === TrackChangeType.MODIFIED
        const isReplaced = changeType === TrackChangeType.REPLACED

        const cardType: CardType = {
            isAdded: suggestionType === SUGGESTION_TYPE_MAP.INSERTION,
            isReplaced: suggestionType === SUGGESTION_TYPE_MAP.REPLACED,
            isDeleted: suggestionType === SUGGESTION_TYPE_MAP.DELETION,
            isApprovedAdded: isApproved && isChangeTypeAdded,
            isApprovedDeleted: isApproved && isChangeTypeDeleted,
            isRejectedAdded: isRejected && isChangeTypeAdded,
            isRejectedDeleted: isRejected && isChangeTypeDeleted,
            isDisabled: suggestionType === SUGGESTION_TYPE_MAP.DISABLED,
            isApprovedOptionGroup: isChangeTypeOptionGroup && isApproved,
            isRejectedOptionGroup: isChangeTypeOptionGroup && isRejected,
            isOptionGroup: isChangeTypeOptionGroup,
            isModified: suggestionType === SUGGESTION_TYPE_MAP.MODIFIED,
            isModifiedApproved: isChangeTypeModified && isApproved,
            isModifiedRejected: isChangeTypeModified && isRejected,
            isReplacedApproved: isReplaced && isApproved,
            isReplacedRejected: isReplaced && isRejected,
        }

        // Calculate derived value
        const isAddedCardType =
            cardType.isAdded ||
            cardType.isApprovedAdded ||
            cardType.isRejectedAdded ||
            cardType.isModified

        return { cardType, isAddedCardType }
    }, [cardSuggestion?.type, trackChanges?.changeType, trackChanges?.status])

    const isOptionGroupTrackChange = useMemo(() => {
        return trackChanges?.changeType === TrackChangeType.OPTIONGROUP
    }, [trackChanges])

    // Get styles with proper dependencies
    const classes = useTrackChangesCardStyles(
        useMemo(
            () => ({
                isAdded: isAddedCardType,
                isNotDefaultSuggestionCardFilter,
                isOptionGroupTrackChange,
            }),
            [
                isAddedCardType,
                isNotDefaultSuggestionCardFilter,
                isOptionGroupTrackChange,
            ]
        )
    )

    return { cardType, classes }
}
